import { useRef, useState, useEffect } from 'react';

interface BelgianNRNInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  onBlur?: () => void;
  /**
   * Les six premiers chiffres attendus (AAMMJJ, la date de naissance). Ils sont
   * proposés quand on entre dans la première case encore vide.
   */
  prefixe?: string;
}

const LONGUEURS = [6, 3, 2] as const;

/** Les trois cases à partir d'un numéro complet ou partiel. */
const decouper = (nrn: string): [string, string, string] => [
  nrn.slice(0, 6),
  nrn.slice(6, 9),
  nrn.slice(9, 11),
];

/**
 * Le numéro de registre national en trois cases (AAMMJJ · 123 · 12).
 *
 * ## Pourquoi les cases gardent leur propre état
 *
 * Avant, chaque frappe recomposait le numéro puis le redécoupait aussitôt à
 * partir de la valeur reçue. Effacer un chiffre dans la première case faisait
 * donc glisser tout le reste vers la gauche : « 200319 · 152 · 12 » devenait
 * « 203191 · 521 · 2 », puis « Le numéro compte 11 chiffres ». Les cases ne se
 * resynchronisent plus que quand la valeur change de l'extérieur.
 *
 * ## Coller le numéro d'un bloc
 *
 * Un parent copie souvent le numéro tel qu'il est imprimé (« 20.03.19-152.12 ») :
 * tout collage de plus de six chiffres est réparti sur les trois cases.
 */
const BelgianNRNInput = ({ value, onChange, error, onBlur, prefixe }: BelgianNRNInputProps) => {
  const [cases, setCases] = useState<[string, string, string]>(() => decouper(value || ''));
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Resynchroniser seulement si la valeur vient d'ailleurs (chargement, effacement)
  useEffect(() => {
    if ((value || '') !== cases.join('')) setCases(decouper(value || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const publier = (suivantes: [string, string, string]) => {
    setCases(suivantes);
    onChange(suivantes.join(''));
  };

  const saisir = (index: 0 | 1 | 2, brut: string) => {
    const chiffres = brut.replace(/\D/g, '');

    // Un numéro entier collé ou dicté dans une case : on le répartit
    if (chiffres.length > LONGUEURS[index] && chiffres.length >= 7) {
      publier(decouper(chiffres.slice(0, 11)));
      refs[2].current?.focus();
      return;
    }

    const suivantes = [...cases] as [string, string, string];
    suivantes[index] = chiffres.slice(0, LONGUEURS[index]);
    publier(suivantes);
    if (suivantes[index].length === LONGUEURS[index] && index < 2) {
      refs[index + 1].current?.focus();
    }
  };

  const proposerPrefixe = () => {
    if (prefixe && /^\d{6}$/.test(prefixe) && !cases.join('')) {
      publier([prefixe, '', '']);
      refs[1].current?.focus();
    }
  };

  const reculer = (e: React.KeyboardEvent<HTMLInputElement>, index: 0 | 1 | 2) => {
    if (e.key === 'Backspace' && !cases[index] && index > 0) {
      e.preventDefault();
      refs[index - 1].current?.focus();
    }
  };

  const champ = (index: 0 | 1 | 2, placeholder: string, largeur: string, label: string) => (
    <input
      ref={refs[index]}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={label}
      aria-invalid={!!error}
      placeholder={placeholder}
      className={`form-input ${largeur} ${error ? 'border-red-500' : ''}`}
      value={cases[index]}
      onChange={(e) => saisir(index, e.target.value)}
      onPaste={(e) => {
        const colle = e.clipboardData.getData('text');
        if (colle.replace(/\D/g, '').length >= 7) {
          e.preventDefault();
          saisir(index, colle);
        }
      }}
      onFocus={index === 0 ? proposerPrefixe : undefined}
      onKeyDown={(e) => reculer(e, index)}
      onBlur={onBlur}
    />
  );

  return (
    <div>
      <div className="flex items-center space-x-1">
        {champ(0, 'AAMMJJ', 'w-24', 'Date de naissance (AAMMJJ)')}
        {champ(1, '123', 'w-16', 'Numéro d’ordre')}
        {champ(2, '12', 'w-12', 'Chiffres de contrôle')}
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default BelgianNRNInput;
